import type { ApexOptions } from 'apexcharts'
import type { UserMetricPeriod } from '../../features/dashboard/domain/models/dashboard'
import { buildLineChartOptions, ChartColors, type ChartSeriesItem } from '../shared/charts'
import { dayFormatter, fullDateFormatter, monthYearFormatter } from '../shared/charts/config/chart-formatters'

export interface DashboardLegendState {
  [key: string]: boolean
  uniqueVisitors: boolean
  newSubscribedUsers: boolean
  churnedUsers: boolean
  annualSubscribers: boolean
  monthlySubscribers: boolean
}

export interface DashboardLegendItem {
  key: string
  label: string
  color: string
}

const DASHBOARD_COLORS = {
  uniqueVisitors: ChartColors.blue,
  newSubscribedUsers: ChartColors.orange,
  churnedUsers: ChartColors.redBright,
  annualSubscribers: ChartColors.cyan,
  monthlySubscribers: ChartColors.green
}

export const DASHBOARD_LEGEND_ITEMS: DashboardLegendItem[] = [
  { key: 'uniqueVisitors', label: 'Unique Visitors', color: DASHBOARD_COLORS.uniqueVisitors },
  { key: 'newSubscribedUsers', label: 'New Subscribed Users', color: DASHBOARD_COLORS.newSubscribedUsers },
  { key: 'churnedUsers', label: 'Churned Users', color: DASHBOARD_COLORS.churnedUsers },
  { key: 'annualSubscribers', label: 'Annual Subscribers', color: DASHBOARD_COLORS.annualSubscribers },
  { key: 'monthlySubscribers', label: 'Monthly Subscribers', color: DASHBOARD_COLORS.monthlySubscribers }
]

export const DASHBOARD_INITIAL_LEGEND_STATE: DashboardLegendState = {
  uniqueVisitors: true,
  newSubscribedUsers: true,
  churnedUsers: true,
  // Off by default: these are a subscriber base in the hundreds or thousands,
  // against flows in the tens. Drawn together the flow lines flatten out.
  annualSubscribers: false,
  monthlySubscribers: false
}

const detectFormatter = (dates: string[]): ((dateStr: string) => string) => {
  if (dates.length < 2) return monthYearFormatter
  const d0 = new Date(`${dates[0]}T00:00:00`)
  const d1 = new Date(`${dates[1]}T00:00:00`)
  const diffDays = Math.round((d1.getTime() - d0.getTime()) / (24 * 60 * 60 * 1000))
  return diffDays > 15 ? monthYearFormatter : dayFormatter
}

export const getDashboardChartOptions = (periods: UserMetricPeriod[], legend: DashboardLegendState): ApexOptions => {
  const dates = periods.map((p) => p.date)
  const formatCategory = detectFormatter(dates)

  const series: ChartSeriesItem[] = []

  if (legend.uniqueVisitors) {
    series.push({
      name: 'Unique Visitors',
      data: periods.map((p) => p.uniqueVisitors),
      color: DASHBOARD_COLORS.uniqueVisitors
    })
  }

  if (legend.newSubscribedUsers) {
    series.push({
      name: 'New Subscribed Users',
      data: periods.map((p) => p.newSubscribedUsers),
      color: DASHBOARD_COLORS.newSubscribedUsers
    })
  }

  if (legend.churnedUsers) {
    series.push({
      name: 'Churned Users',
      data: periods.map((p) => p.churnedUsers),
      color: DASHBOARD_COLORS.churnedUsers
    })
  }

  if (legend.annualSubscribers) {
    series.push({
      name: 'Annual Subscribers',
      data: periods.map((p) => p.annualSubscribers),
      color: DASHBOARD_COLORS.annualSubscribers
    })
  }

  if (legend.monthlySubscribers) {
    series.push({
      name: 'Monthly Subscribers',
      data: periods.map((p) => p.monthlySubscribers),
      color: DASHBOARD_COLORS.monthlySubscribers
    })
  }

  const categories = dates.map((d) => formatCategory(d))

  return buildLineChartOptions({
    series,
    categories,
    tooltipHeaderFormatter: (label) => {
      const index = categories.indexOf(label)
      const match = index >= 0 ? dates[index] : undefined
      return match ? fullDateFormatter(match) : label
    }
  })
}
